import type { FleetUnit } from '@chukua/common';
import { FileX, ShieldAlert } from 'lucide-react';
import { Navigate, useParams } from 'react-router';
import { DetailPageSkeleton } from '../../../../../shared/components/DetailPageSkeleton';
import { EmptyState } from '../../../../../shared/components/EmptyState';
import { TrailerDetail } from '../../trailers/components/TrailerDetail';
import { UnitDetail } from '../components/UnitDetail';
import { UNITS_TRAIL } from '../components/units-trail';
import { useFleetUnit } from '../hooks/use-fleet-unit';
import { isSpareTrailerTab, UNIT_TABS, unitPath, type UnitTab } from '../units.paths';

/**
 * `/fleet/units/<registration>/<tab>` (gitaumoses4/chukua#390). A truck's registration opens its unit; a coupled trailer's
 * opens the unit of the truck pulling it, on the same tab; a spare trailer's opens the
 * trailer's own page.
 */
export function UnitPage({ tab }: { tab: UnitTab }) {
  const { registration = '' } = useParams();
  const { state, reload } = useFleetUnit(registration);

  if (state.status === 'forbidden') return <Navigate to="/" replace />;
  if (state.status === 'loading') return <DetailPageSkeleton label="Loading unit" breadcrumbs={UNITS_TRAIL} tabs={UNIT_TABS.length} />;
  if (state.status === 'not_found') {
    return (
      <EmptyState
        icon={FileX}
        title="No such truck or trailer"
        description={`Nothing in the fleet is registered ${registration}, or the link is out of date.`}
      />
    );
  }
  if (state.status === 'error') {
    return <EmptyState icon={ShieldAlert} title="Couldn't load this unit" description="Something went wrong loading this unit. Try reloading the page." />;
  }

  return <ResolvedUnit unit={state.unit} tab={tab} onChanged={() => void reload()} />;
}

/** What a registration names, once known: a truck's unit, the unit pulling a coupled trailer, or a spare trailer. */
function ResolvedUnit({ unit, tab, onChanged }: { unit: FleetUnit; tab: UnitTab; onChanged: () => void }) {
  const { truck, trailer, matched } = unit;
  if (truck && matched === 'trailer') return <Navigate to={unitPath(truck.registration, tab)} replace />;
  if (truck) return <UnitDetail key={truck.id} truck={truck} trailer={trailer} tab={tab} onUnitChanged={onChanged} />;
  if (!trailer) return null;
  if (!isSpareTrailerTab(tab)) return <Navigate to={unitPath(trailer.registration, 'details')} replace />;
  return <TrailerDetail key={trailer.id} trailerId={trailer.id} tab={tab} onCoupled={onChanged} />;
}
